import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import { REELS_PLAN_DAY_PRESETS, REELS_PLAN_MAX_DAYS, REELS_PLAN_MIN_DAYS } from 'fym-shared/src/music24/reelsPlan';
import { TypedMemo } from '../../../../../common/types';
import { reelsPlanMessages } from './reelsPlanMessages';
import { ReelsPlanSegment } from './ReelsPlanSegment';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the AI reels plan; each part uses its own slice of it */
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

const OTHER = 'other';

interface ReelsPlanLengthPickerProps {
  /** The length in days. Not a number while the "Other" field is empty. */
  days: number;
  disabled: boolean;
  onChange: (days: number) => void;
}

/**
 * "Length" of the setup (design rollout-plan f1): 7, 14 or 30 days, or "Other" with a number field
 * (3 to 60). A number outside the range goes up as it is; the setup says the range and sends no
 * dry run for it.
 */
const ReelsPlanLengthPickerComponent: React.FC<ReelsPlanLengthPickerProps> = ({ days, disabled, onChange }) => {
  const intl = useIntl();
  const [isOther, setIsOther] = useState(() => !REELS_PLAN_DAY_PRESETS.includes(days));
  const [text, setText] = useState(() => (Number.isFinite(days) ? String(days) : ''));
  const options = useMemo(
    () => [
      ...REELS_PLAN_DAY_PRESETS.map((preset) => ({
        value: String(preset),
        label: intl.formatMessage(reelsPlanMessages.lengthDays, { days: preset }),
      })),
      { value: OTHER, label: intl.formatMessage(reelsPlanMessages.lengthOther) },
    ],
    [intl]
  );

  const pick = useCallback(
    (value: string) => {
      if (value === OTHER) {
        setIsOther(true);
        setText(Number.isFinite(days) ? String(days) : '');
        return;
      }
      setIsOther(false);
      onChange(Number(value));
    },
    [days, onChange]
  );
  const type = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
      onChange(event.target.value.trim() === '' ? Number.NaN : Number(event.target.value));
    },
    [onChange]
  );

  return (
    <ReelsPlanSegment
      label={intl.formatMessage(reelsPlanMessages.length)}
      options={options}
      value={isOther ? OTHER : String(days)}
      disabled={disabled}
      onChange={pick}
    >
      {isOther && (
        <Input
          type="number"
          inputMode="numeric"
          bsSize="sm"
          className={clsx(styles.field, styles.number)}
          min={REELS_PLAN_MIN_DAYS}
          max={REELS_PLAN_MAX_DAYS}
          step={1}
          value={text}
          disabled={disabled}
          aria-label={intl.formatMessage(reelsPlanMessages.lengthOtherLabel)}
          onChange={type}
        />
      )}
    </ReelsPlanSegment>
  );
};

export const ReelsPlanLengthPicker = TypedMemo(ReelsPlanLengthPickerComponent);
